import React, { useEffect, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { useTranslation } from "react-i18next";
import { arSD } from "@mui/material/locale";
import { themeOptions } from "../utils/theme";
import dayjs from "../utils/dayjs";

const cache = createCache({ key: "mui-ltr" });

/**
 * "#5B648F" -> "91 100 143".
 *
 * A bare space-separated triple, because that is the only form that works for BOTH
 * `rgb(var(--x) / 20%)` in the stylesheet and Tailwind's `<alpha-value>` placeholder.
 * Wrapping it in rgb() here would silently break every opacity modifier in the app.
 *
 * This is parsing, not colour maths — the arithmetic that decides WHICH colours these are
 * lives on the server, deliberately, so the browser and the desktop till cannot drift apart.
 */
const toRgbChannels = (hex) => {
  if (typeof hex !== "string") return null;

  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;

  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

/**
 * Paints the organisation's accent onto the document, or clears it back to the defaults.
 *
 * Setting a custom property on documentElement overrides the `:root` rule in styles/index.css,
 * and removing it restores that rule — which is why "no colour chosen" needs no separate
 * default palette here. The stylesheet is the default.
 */
const applyAccent = (accent) => {
  const root = document.documentElement;

  const set = (name, value) => {
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  };

  const light = accent?.light;
  const ramp = accent?.ramp;

  set("--primary-rgb", toRgbChannels(light?.fill));
  set("--primary-contrast-color", light?.onFill || null);

  // The nine Tailwind steps. Step 500 is the fill itself, so the scale and the buttons
  // are the same colour rather than two colours that happen to be close.
  [50, 100, 200, 300, 400, 500, 600, 700, 800, 900].forEach((step) =>
    set(`--primary-${step}-rgb`, toRgbChannels(ramp?.[`s${step}`]))
  );
};

const ThemeLocaleProvider = ({ children, uiTemplate = "modern", accentTheme = null }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    document.documentElement.dir = "ltr";
    document.documentElement.lang = i18n.language;
    const langMap = { ar: "ar" };
    dayjs.locale(langMap[i18n.language] || "en");
  }, [isArabic, i18n.language]);

  // Drive the template's design tokens (radius/density/shadows) via a root
  // data-attribute — see :root[data-ui-template="modern"] in styles/index.css.
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-ui-template",
      uiTemplate || "classic"
    );
  }, [uiTemplate]);

  // The organisation's accent, through the same seam and for the same reason: one place
  // that turns a saved setting into something the whole stylesheet can see.
  useEffect(() => {
    applyAccent(accentTheme);
  }, [accentTheme]);

  const theme = useMemo(() => {
    // MUI resolves palette colours in JS, so it cannot read the CSS variables above —
    // components like Button and Switch compute hover and ripple shades from palette.primary
    // at theme-build time. The accent therefore has to be merged in here as well, and the two
    // must agree. `contrastText` comes from the server's derived onFill rather than being
    // assumed white: a yellow or teal brand needs near-black, and MUI would otherwise put
    // white text on it.
    const accented = accentTheme?.light?.fill
      ? {
          ...themeOptions,
          palette: {
            ...themeOptions.palette,
            primary: {
              main: accentTheme.light.fill,
              dark: accentTheme.light.fillPressed,
              light: accentTheme.light.subtle,
              contrastText: accentTheme.light.onFill,
            },
            primaryXS: {
              main: accentTheme.light.subtle,
              contrastText: accentTheme.light.ink,
            },
          },
        }
      : themeOptions;

    return isArabic ? createTheme(accented, arSD) : createTheme(accented);
  }, [isArabic, accentTheme]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
};

export default ThemeLocaleProvider;
